<script setup lang="ts">
/**
 * 입시 소식 상세 `/news/:id` (083 US1, FR-011·012·023).
 * 제목·게시일·수정일(수정된 경우)·기준일·본문·출처 + 다른 소식 + (비로그인일 때만) 가입·로그인 유도.
 * 분석 기능 진입 유도는 두지 않는다(FR-012 — #358 해결 후 연다).
 * 없거나 미게시·삭제·공지 번호(404)면 안내 + 목록 이동(+ noindex — usePageNotFound).
 * canonical 은 색인 정책(seoRoutes `/^\/news\/\d+$/`)에 따라 useCanonicalHead 가 발행한다.
 * 조회수·GA4 는 브라우저에서 글이 표시될 때만 보낸다(onMounted 이후 — 프리렌더 미집계).
 */
import { useQuery } from '@tanstack/vue-query';
import { useHead } from '@unhead/vue';
import { ArrowLeft, Loader2, Newspaper } from 'lucide-vue-next';
import { computed, onMounted, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { fetchNews, increaseNewsView } from '#/api/news';
import ArticleBody from '#/components/fo/board/ArticleBody.vue';
import NewsSignupPrompt from '#/components/fo/board/NewsSignupPrompt.vue';
import RelatedNews from '#/components/fo/board/RelatedNews.vue';
import SourceAttribution from '#/components/fo/board/SourceAttribution.vue';
import { BaseButton, buttonVariants, EmptyState, PageShell, PageTitle } from '#/components/ui';
import { usePageNotFound } from '#/composables/usePageNotFound';
import { buildDocumentTitle } from '#/composables/useRouteHead';
import { trackNewsDetailView } from '#/lib/analytics';
import { formatDateOnly, formatKstDate } from '#/lib/datetime';
import { publicQueryKeys } from '#/queries/publicQueries';

const route = useRoute();
/** 라우트 :id — 숫자가 아니면 NaN → 백엔드 404 → not-found. */
const newsId = computed(() => Number(route.params.id));

const {
  data: news,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: computed(() => publicQueryKeys.news(newsId.value)),
  queryFn: () => fetchNews(newsId.value),
});

/** 404(null)만 데이터 없음 — 로딩 전(undefined)·네트워크 오류는 아니다. */
usePageNotFound(() => news.value === null);

/** 게시 후 내용이 바뀐 경우에만 수정일을 보인다(같은 날짜면 생략). */
const updatedDate = computed(() => {
  if (!news.value) return '';
  const published = formatKstDate(news.value.publishedAt);
  const updated = formatKstDate(news.value.updatedAt);
  return updated && updated !== published ? updated : '';
});

/** 글이 표시될 때 id 당 1회 — 같은 화면에서 다른 소식으로 이동하면 새 id 로 다시 센다. */
onMounted(() => {
  watch(
    () => news.value?.id,
    (id) => {
      if (id == null) return;
      void increaseNewsView(id);
      trackNewsDetailView(id);
    },
    { immediate: true },
  );
});

/** 글별 제목·설명 — description 은 운영자가 쓴 요약(검색 결과 문구). */
useHead({
  title: () => (news.value ? buildDocumentTitle(`${news.value.title} · 입시 소식`) : undefined),
  meta: () => (news.value ? [{ name: 'description', content: news.value.summary }] : []),
});
</script>

<template>
  <PageShell variant="mypage" width="detail">
    <PageTitle eyebrow="입시 소식" title="입시 소식" />

    <RouterLink
      to="/news/"
      class="mb-5 inline-flex items-center gap-1.5 text-[13.5px] font-bold text-muted transition-colors hover:text-brand"
    >
      <ArrowLeft class="h-4 w-4" />
      입시 소식 목록
    </RouterLink>

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center gap-3 py-20 text-muted"
      role="status"
    >
      <Loader2 class="h-7 w-7 animate-spin text-brand-muted" />
      <span class="text-[13.5px]">입시 소식을 불러오는 중입니다…</span>
    </div>

    <EmptyState
      v-else-if="isError"
      title="입시 소식을 불러오지 못했습니다"
      description="잠시 후 다시 시도해 주세요."
    >
      <template #action>
        <BaseButton variant="line" size="md" @click="() => refetch()">다시 시도</BaseButton>
      </template>
    </EmptyState>

    <EmptyState
      v-else-if="!news"
      title="글을 찾을 수 없습니다"
      description="삭제되었거나 존재하지 않는 입시 소식입니다."
    >
      <template #icon><Newspaper class="h-7 w-7" :stroke-width="1.6" /></template>
      <template #action>
        <RouterLink to="/news/" :class="buttonVariants({ variant: 'line', size: 'md' })">
          입시 소식 목록으로
        </RouterLink>
      </template>
    </EmptyState>

    <template v-else>
      <article class="rounded-card border border-line bg-white p-5 shadow-sm md:p-7">
        <h2
          class="font-serif text-[19px] font-extrabold tracking-[-0.02em] text-brand md:text-[21px]"
        >
          {{ news.title }}
        </h2>
        <p class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-muted">
          <span>게시일 {{ formatKstDate(news.publishedAt) }}</span>
          <span v-if="updatedDate">수정일 {{ updatedDate }}</span>
          <span>자료 기준일 {{ formatDateOnly(news.basisDate) }}</span>
        </p>

        <ArticleBody class="mt-6" :html="news.content" />

        <div class="mt-7 border-t border-line pt-4">
          <SourceAttribution :sources="news.sources" />
        </div>
      </article>

      <NewsSignupPrompt :news-id="news.id" />
      <RelatedNews :current-id="news.id" />
    </template>
  </PageShell>
</template>
